<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <!-- Skip Navigation Link for Keyboard Users -->
  <a href="#main-content" class="skip-link">
    {{ $t('navigation.skipToMain') }}
  </a>

  <nav v-if="!shouldHideNavigation" class="app-navigation" aria-label="Main navigation">
    <div class="nav-container">
      <!-- Logo/Brand -->
      <router-link to="/" class="nav-brand">
        <NoorWriterLogo variant="main" size="large" theme="auto" :clickable="true" />
      </router-link>

      <!-- Navigation Links -->
      <div class="nav-links">
        <router-link to="/editor" class="nav-link">
          {{ $t('navigation.editor') }}
        </router-link>
        <router-link to="/?showHome=true" class="nav-link" v-if="isAuthenticated && isPremiumUser">
          {{ $t('navigation.home') }}
        </router-link>
        <router-link to="/pricing" class="nav-link" v-if="!isAuthenticated || !isPremiumUser">
          {{ $t('navigation.pricing') }}
        </router-link>
        <router-link to="/settings" class="nav-link" v-if="isAuthenticated">
          {{ $t('navigation.settings') }}
        </router-link>
        <router-link to="/support" class="nav-link" v-if="!isAuthenticated">
          {{ $t('navigation.support') }}
        </router-link>
        <router-link to="/extension" class="nav-link nav-link-extension" v-if="!isAuthenticated">
          {{ $t('navigation.extension') }}
        </router-link>
      </div>

      <!-- Authentication Actions -->
      <div class="nav-auth">
        <template v-if="!isAuthenticated">
          <router-link to="/auth/signin" class="nav-link auth-link">
            {{ $t('auth.signIn') }}
          </router-link>
          <router-link to="/auth/signup" class="btn btn-primary">
            {{ $t('auth.signUp') }}
          </router-link>
        </template>
        <template v-else>
          <!-- User Menu Dropdown (Issue: Implement dropdown for better organization) -->
          <UserMenuDropdown
            :user-name="user?.name || user?.email || ''"
            :user-email="user?.email || ''"
            :is-premium="isPremiumUser"
            :is-signing-out="isSigningOut"
            @sign-out="showSignOutConfirmation"
          />
        </template>
      </div>

      <!-- Mobile Menu Toggle (with haptic feedback) -->
      <button
        class="mobile-menu-toggle"
        @click="handleMobileMenuToggle"
        :class="{ active: isMobileMenuOpen }"
        :aria-expanded="isMobileMenuOpen"
        :aria-label="$t('navigation.toggleMenu')"
        aria-controls="mobile-menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
    </div>

    <!-- Mobile Menu Backdrop -->
    <div v-if="isMobileMenuOpen" class="mobile-menu-backdrop" @click="closeMobileMenu"></div>

    <!-- Mobile Menu -->
    <div
      id="mobile-menu"
      class="mobile-menu"
      :class="{ open: isMobileMenuOpen }"
      role="navigation"
      :aria-hidden="!isMobileMenuOpen"
      :aria-label="$t('navigation.mobileMenu')"
    >
      <div class="mobile-menu__container">
        <router-link to="/editor" class="mobile-nav-link" @click="handleMobileNavClick">
          {{ $t('navigation.editor') }}
        </router-link>
        <router-link
          to="/?showHome=true"
          class="mobile-nav-link"
          v-if="isAuthenticated && isPremiumUser"
          @click="handleMobileNavClick"
        >
          {{ $t('navigation.home') }}
        </router-link>
        <router-link
          to="/pricing"
          class="mobile-nav-link"
          v-if="!isAuthenticated || !isPremiumUser"
          @click="handleMobileNavClick"
        >
          {{ $t('navigation.pricing') }}
        </router-link>
        <router-link
          to="/settings"
          class="mobile-nav-link"
          v-if="isAuthenticated"
          @click="handleMobileNavClick"
        >
          {{ $t('navigation.settings') }}
        </router-link>
        <router-link to="/support" class="mobile-nav-link" v-if="!isAuthenticated" @click="handleMobileNavClick">
          {{ $t('navigation.support') }}
        </router-link>
        <router-link to="/extension" class="mobile-nav-link mobile-nav-link-extension" v-if="!isAuthenticated" @click="handleMobileNavClick">
          <svg class="extension-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-.84 4.67h7.49l-3.87 6.7c-.51-.05-1.04-.08-1.58-.08-2.29 0-4.42.78-6.11 2.08l2.07-3.59c1.17-2.03 3.37-3.29 5.73-3.29.76 0 1.5.11 2.2.31l-5.94-.13zm-6.3 2.89l3.87 6.7c-.92 1.15-1.47 2.61-1.47 4.21 0 .58.07 1.14.21 1.68l-4.02-6.96c-.39-.68-.6-1.45-.6-2.26 0-2.43 1.97-4.4 4.4-4.4.23 0 .45.02.67.05l-3.06.98zm8.14 2.31c3.02 0 5.47 2.45 5.47 5.47s-2.45 5.47-5.47 5.47-5.47-2.45-5.47-5.47 2.45-5.47 5.47-5.47z"/>
          </svg>
          {{ $t('navigation.extension') }}
          <span class="extension-badge">New</span>
        </router-link>

        <div class="mobile-auth">
          <template v-if="!isAuthenticated">
            <router-link to="/auth/signin" class="mobile-nav-link" @click="closeMobileMenu">
              {{ $t('auth.signIn') }}
            </router-link>
            <router-link
              to="/auth/signup"
              class="btn btn-primary mobile-signup"
              @click="closeMobileMenu"
            >
              {{ $t('auth.signUp') }}
            </router-link>
          </template>
          <template v-else>
            <div class="mobile-user-info">
              <span class="mobile-user-name">{{ user?.name || user?.email }}</span>
              <span class="mobile-user-plan" :class="isPremiumUser ? 'premium' : 'free'">
                {{ isPremiumUser ? $t('subscription.premium') : $t('subscription.free') }}
              </span>
              <button
                @click="showSignOutConfirmation"
                class="mobile-sign-out-btn"
                :disabled="isSigningOut"
                :aria-busy="isSigningOut"
              >
                <span class="spinner-icon" v-if="isSigningOut"></span>
                {{ isSigningOut ? $t('auth.signingOut') : $t('auth.signOut') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </nav>

  <!-- Sign-Out Confirmation Dialog (Issue: Add confirmation before sign-out) -->
  <ConfirmDialog
    v-model:is-open="isSignOutDialogOpen"
    :title="$t('auth.confirmSignOut.title')"
    :message="$t('auth.confirmSignOut.message')"
    :confirm-text="$t('auth.confirmSignOut.confirm')"
    :cancel-text="$t('auth.confirmSignOut.cancel')"
    :processing-text="$t('auth.signingOut')"
    :is-processing="isSigningOut"
    @confirm="handleSignOut"
    @cancel="isSignOutDialogOpen = false"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSubscriptionStore } from '@/stores/subscription'
import NoorWriterLogo from '@/components/NoorWriterLogo.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import UserMenuDropdown from '@/components/UserMenuDropdown.vue'
import { useHaptics } from '@/utils/haptics'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const subscriptionStore = useSubscriptionStore()
const haptics = useHaptics()

// Reactive state
const isMobileMenuOpen = ref(false)
const isSigningOut = ref(false) // Loading state for sign-out action
const isSignOutDialogOpen = ref(false) // Sign-out confirmation dialog state

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)
const isPremiumUser = computed(() => subscriptionStore.isPremium)

// Hide navigation when authenticated user is in the editor
const shouldHideNavigation = computed(() => {
  return isAuthenticated.value && route.path === '/editor'
})

// Functions

/**
 * Toggle mobile menu with haptic feedback (Issue: Add haptic feedback)
 */
function handleMobileMenuToggle() {
  // Trigger haptic feedback on mobile devices
  haptics.medium()

  // Toggle menu state
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

/**
 * Handle mobile navigation link click with haptic feedback
 */
function handleMobileNavClick() {
  // Trigger haptic feedback on mobile devices
  haptics.light()

  // Close mobile menu
  closeMobileMenu()
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false
}

/**
 * Show sign-out confirmation dialog (Issue: Add confirmation before sign-out)
 */
function showSignOutConfirmation() {
  // Trigger haptic feedback for important action
  haptics.heavy()

  // Show confirmation dialog
  isSignOutDialogOpen.value = true
}

/**
 * Handle user sign-out with loading state
 * Shows loading indicator during the async operation
 */
async function handleSignOut() {
  // Prevent multiple simultaneous sign-out attempts
  if (isSigningOut.value) return

  try {
    isSigningOut.value = true
    await authStore.signOut()
    closeMobileMenu()
    router.push('/')
  } catch (error) {
    // Error handling is managed by the auth store
    console.error('Sign out error:', error)
  } finally {
    isSigningOut.value = false
  }
}

// Handle escape key to close mobile menu
function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isMobileMenuOpen.value) {
    closeMobileMenu()
  }
}

// Auto-close mobile menu on route change (Issue 4.2)
// This improves UX by automatically closing the menu when user navigates
watch(
  () => route.path,
  () => {
    if (isMobileMenuOpen.value) {
      closeMobileMenu()
    }
  }
)

// Add/remove event listeners
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style lang="scss" scoped>
// Skip Navigation Link - Hidden by default, visible on focus
// This is a common accessibility pattern for keyboard navigation
.skip-link {
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  z-index: calc(var(--nw-z-sticky) + 1);

  // Styling for when the link is focused
  background: var(--nw-primary);
  color: var(--nw-white);
  padding: 0.75rem 1.5rem;
  text-decoration: none;
  font-weight: 600;
  border-radius: 0 0 var(--nw-radius-md) 0;
  transition: all 0.2s ease;
  white-space: nowrap;

  // Make visible and properly positioned when focused
  &:focus {
    position: fixed;
    left: 0;
    top: 0;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    outline: 3px solid var(--nw-white);
    outline-offset: -3px;
    box-shadow: var(--nw-shadow-lg);
  }
}

.app-navigation {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: var(--nw-z-sticky);
  transition: all 0.3s ease;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  position: relative;
}

.nav-brand {
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
  border-radius: var(--nw-radius-sm);

  &:hover {
    transform: scale(1.05);
  }

  // Focus indicator for keyboard navigation
  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: 4px;
    box-shadow: var(--nw-focus-ring);
  }
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  // Tablet landscape: reduce spacing (Issue 3.1)
  @media (max-width: 1024px) and (min-width: 769px) {
    gap: 0.25rem;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.nav-link {
  text-decoration: none;
  color: var(--nw-text-secondary);
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
  border-radius: var(--nw-radius-lg);
  position: relative;

  &:hover {
    color: var(--nw-primary);
    background: var(--nw-primary-10);
  }
  
  // Animated underline effect on hover
  &::after {
    content: '';
    position: absolute;
    bottom: 0.25rem;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    border-radius: 1px;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }
  
  &:hover::after {
    width: 60%;
  }

  // Focus indicator for keyboard navigation
  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: 2px;
    box-shadow: var(--nw-focus-ring);
  }

  &.router-link-active {
    color: var(--nw-primary);
    font-weight: 600;
    background: var(--nw-primary-10);
    
    &::after {
      width: 60%;
    }
  }

  &.auth-link {
    color: var(--nw-primary);
    font-weight: 600;
  }
}

// Extension link with icon and badge
.nav-link-extension {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  position: relative;

  .extension-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .extension-badge {
    display: inline-block;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    margin-left: 0.25rem;
    letter-spacing: 0.025em;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:hover .extension-badge {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
  }
}

.nav-auth {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    display: none;
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: var(--nw-radius-xl);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;

  &-primary {
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: var(--nw-white);
    box-shadow: 0 4px 14px rgba(30, 64, 175, 0.35);

    &:hover {
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 6px 20px rgba(30, 64, 175, 0.45);
    }

    // Focus indicator for keyboard navigation
    &:focus-visible {
      outline: 2px solid var(--nw-primary-dark);
      outline-offset: 2px;
      box-shadow: var(--nw-focus-ring);
    }
  }
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 1rem;

  // Tablet landscape: reduce gap to prevent overflow (Issue 1.2)
  @media (max-width: 1024px) and (min-width: 769px) {
    gap: 0.75rem;
  }

  // Smaller desktop screens: adjust layout (Issue 1.2)
  @media (max-width: 1200px) and (min-width: 1025px) {
    gap: 0.875rem;
  }
}

.user-name {
  font-weight: 500;
  color: var(--nw-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;

  // Tablet landscape: reduce max-width (Issue 1.2)
  @media (max-width: 1024px) and (min-width: 769px) {
    max-width: 120px;
    font-size: 0.9rem;
  }
}

.user-plan {
  padding: 0.25rem 0.5rem;
  border-radius: var(--nw-radius-xl);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;

  &.free {
    background: var(--nw-secondary);
    color: var(--nw-text-inverse);
  }

  &.premium {
    background: var(--nw-success);
    color: var(--nw-text-inverse);
  }

  // Tablet landscape: slightly smaller (Issue 1.2)
  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
}

.sign-out-btn {
  background: transparent;
  border: 1px solid var(--nw-error);
  color: var(--nw-error);
  padding: 0.25rem 0.75rem;
  border-radius: var(--nw-radius-sm);
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--nw-error);
    color: var(--nw-text-inverse);
  }

  // Disabled state for loading (Issue 4.3)
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--nw-bg-secondary);
    border-color: var(--nw-border-light);
    color: var(--nw-text-secondary);
  }

  // Focus indicator for keyboard navigation
  &:focus-visible {
    outline: 2px solid var(--nw-error);
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
  }

  // Tablet landscape: smaller font (Issue 1.2)
  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 0.8rem;
    padding: 0.2rem 0.6rem;
  }
}

.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.875rem; // Increased padding for 48px touch target
  min-width: 48px; // Apple HIG minimum touch target (Issue 3.4)
  min-height: 48px; // Apple HIG minimum touch target (Issue 3.4)
  align-items: center;
  justify-content: center;
  position: relative; // For absolute positioning of spans when active
  border-radius: var(--nw-radius-sm);

  @media (max-width: 768px) {
    display: flex;
  }

  // Focus indicator for keyboard navigation
  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: 2px;
    box-shadow: var(--nw-focus-ring);
    background: var(--nw-primary-lightest);
  }

  span {
    width: 20px;
    height: 2px;
    background: var(--nw-text-primary);
    margin: 2px 0;
    transition: all 0.3s ease;
    transform-origin: center center;
    display: block;
  }

  // When active, position spans absolutely to create perfect X
  &.active {
    span {
      position: absolute;
      margin: 0;
      top: 50%;
      left: 50%;
      transform-origin: center center;
    }

    span:nth-child(1) {
      transform: translate(-50%, -50%) rotate(-45deg);
    }

    span:nth-child(2) {
      opacity: 0;
    }

    span:nth-child(3) {
      transform: translate(-50%, -50%) rotate(45deg);
    }
  }
}

.mobile-menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--nw-bg-overlay);
  z-index: var(--nw-z-fixed);

  @media (min-width: 769px) {
    display: none;
  }
}

.mobile-menu {
  display: none;
  background: var(--nw-bg-primary);
  border-top: 1px solid var(--nw-border-light);
  // Use fixed positioning to ensure menu appears above all page content
  position: fixed;
  top: 75px; // Height of the navigation bar
  left: 0;
  right: 0;
  // Higher z-index than navigation and page content to ensure visibility
  z-index: calc(var(--nw-z-fixed) + 1);
  box-shadow: var(--nw-shadow-md);

  &__container {
    padding: 1rem 2rem;
  }

  @media (max-width: 768px) {
    display: block;
    max-height: 0;
    overflow: hidden;
    padding: 0;
    transition:
      max-height 0.3s ease,
      box-shadow 0.3s ease;

    &.open {
      max-height: 500px;
      box-shadow: var(--nw-shadow-lg);
    }
  }
}

.mobile-nav-link {
  display: block;
  padding: 0.75rem 0;
  text-decoration: none;
  color: var(--nw-text-primary);
  font-weight: 500;
  border-bottom: 1px solid var(--nw-border-light);
  transition: all 0.3s ease;
  // Removed opacity to ensure WCAG AA contrast compliance
  border-radius: var(--nw-radius-sm);

  &:hover {
    color: var(--nw-primary);
    padding-left: 0.5rem;
    background: var(--nw-bg-secondary);
  }

  // Focus indicator for keyboard navigation
  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: -2px;
    box-shadow: var(--nw-focus-ring);
    background: var(--nw-primary-lightest);
  }

  &.router-link-active {
    color: var(--nw-primary);
    font-weight: 600;
    border-left: 3px solid var(--nw-primary);
    padding-left: 0.5rem;
    background: var(--nw-primary-lightest);
  }

  &:last-child {
    border-bottom: none;
  }
}

// Mobile extension link with icon and badge
.mobile-nav-link-extension {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .extension-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .extension-badge {
    display: inline-block;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    margin-left: auto;
    letter-spacing: 0.025em;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
}

.mobile-auth {
  margin-top: 1rem;
  padding-top: 1rem;
}

.mobile-signup {
  margin-top: 1rem;
  text-align: center;
}

.mobile-user-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-user-name {
  font-weight: 500;
  color: var(--nw-text-primary);
}

.mobile-user-plan {
  padding: 0.25rem 0.5rem;
  border-radius: var(--nw-radius-xl);
  font-size: 0.75rem;
  font-weight: 600;
  align-self: flex-start;

  &.free {
    background: var(--nw-secondary);
    color: var(--nw-text-inverse);
  }

  &.premium {
    background: var(--nw-success);
    color: var(--nw-text-inverse);
  }
}

.mobile-sign-out-btn {
  background: transparent;
  border: 1px solid var(--nw-error);
  color: var(--nw-error);
  padding: 0.5rem 1rem;
  border-radius: var(--nw-radius-sm);
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  align-self: flex-start;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: var(--nw-error);
    color: var(--nw-text-inverse);
  }

  // Disabled state for loading (Issue 4.3)
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--nw-bg-secondary);
    border-color: var(--nw-border-light);
    color: var(--nw-text-secondary);
  }

  // Focus indicator for keyboard navigation
  &:focus-visible {
    outline: 2px solid var(--nw-error);
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
  }
}

// Spinner icon for loading state (Issue: Add spinner icon)
.spinner-icon {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<!-- Dark theme overrides - unscoped to properly target html.dark -->
<style lang="scss">
html.dark {
  .app-navigation {
    background: rgba(15, 23, 42, 0.95);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    border-bottom: 1px solid #334155;
  }

  .nav-link {
    color: #CBD5E1;

    &:hover {
      color: #3B82F6;
      background: rgba(30, 64, 175, 0.15);
    }

    &.router-link-active {
      color: #3B82F6;
      background: rgba(30, 64, 175, 0.15);
    }

    &.auth-link {
      color: #3B82F6;
    }
  }

  .mobile-menu-toggle {
    span {
      background: #F1F5F9;
    }

    &:focus-visible {
      background: #334155;
    }
  }

  .mobile-menu {
    background: #0F172A;
    border-top-color: #334155;
  }

  .mobile-nav-link {
    color: #F1F5F9;
    border-bottom-color: #334155;

    &:hover {
      color: #3B82F6;
      background: #1E293B;
    }

    &:focus-visible {
      background: #334155;
    }

    &.router-link-active {
      color: #3B82F6;
      background: rgba(30, 64, 175, 0.15);
    }
  }

  .mobile-user-name {
    color: #F1F5F9;
  }
}
</style>
